import React from "react";
import { Info, Stamp } from "lucide-react";
import CommonTooltip from "../common/CommonTooltip";

const CHIP_CLASS =
  "inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100 whitespace-nowrap";

/**
 * Seal numbers as green chips. With `max`, only the first `max` are shown and the
 * rest collapse into a "+N" chip (used in table cells, where space is tight).
 */
export const SealChips = ({ seals, max, className = "", size = "md" }) => {
  const shown = max ? seals.slice(0, max) : seals;
  const hiddenCount = seals.length - shown.length;
  const sizeClass = size === "sm" ? "!text-[11px]" : "";
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {shown.map((seal) => (
        <span key={seal} className={`${CHIP_CLASS} ${sizeClass}`}>
          <Stamp size={11} className="shrink-0" aria-hidden="true" />
          {seal}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span className={`${CHIP_CLASS} ${sizeClass} bg-slate-100 text-slate-600 ring-slate-200`}>
          +{hiddenCount}
        </span>
      )}
    </div>
  );
};

/** Small (i) icon that explains what a seal list means. */
export const SealInfoIcon = ({ title, description }) => (
  <CommonTooltip
    text={title}
    description={description}
    position="top"
    disableDefaultDescription
    className="inline-flex shrink-0"
    tooltipClassName="max-w-[min(20rem,calc(100vw-1.5rem))] whitespace-normal"
  >
    <Info size={12} className="text-slate-400 hover:text-[#236DB4] cursor-help" aria-label={title} />
  </CommonTooltip>
);

/**
 * Labelled block of seal chips with a count and an info tooltip. Shared by the
 * Inventory Details panel and the Meter History drawer so both read the same.
 */
export const SealNumbersPanel = ({ label, seals, infoTitle, infoDescription, emptyText, className = "", testId }) => (
  <div className={`rounded-xl border border-emerald-100 bg-emerald-50/40 px-4 py-3 ${className}`} data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
      <Stamp size={13} className="text-emerald-600" aria-hidden="true" />
      <span>{label}</span>
      {seals.length > 0 && (
        <span className="rounded-full bg-emerald-100 px-1.5 py-px text-[10px] font-bold text-emerald-700">
          {seals.length}
        </span>
      )}
      {infoTitle && <SealInfoIcon title={infoTitle} description={infoDescription} />}
    </div>
    {seals.length ? (
      <SealChips seals={seals} className="mt-2" />
    ) : (
      <div className="mt-1.5 text-xs text-slate-500">{emptyText}</div>
    )}
  </div>
);
